import React, { useContext, useEffect, useState } from "react";
import PagePreview from "./Preview";
import { useSelector } from "react-redux";
import { getData } from "~/functions/gridFunctions";
import PageContainer from "~/components/PageContainer";
import { FullGridContext } from "..";
import { format, parseISO } from "date-fns";
import api from "~/services/api";
import { toast } from "react-toastify";
import ModalFilters, { getStoredFilters } from "./ModalFilters";
import { Pin } from "lucide-react";

export default function PreArrivals() {
  const filial = useSelector((state) => state.auth.filial);
  const defaultOrderBy = { column: "name", asc: true };
  const [selected, setSelected] = useState([]);
  const [enrollmentOpen, setEnrollmentOpen] = useState(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [filtersPinned, setFiltersPinned] = useState(
    () => localStorage.getItem("prearrivals_filters_pinned") === "true"
  );
  const [applyingFilters, setApplyingFilters] = useState(false);
  const [activeFilterQuery, setActiveFilterQuery] = useState(null);
  const defaultGridHeader = [
    {
      title: "Student",
      name: "name",
      type: "text",
      filter: false,
    },
    {
      title: "Status",
      name: "status",
      type: "text",
      filter: false,
    },
    {
      title: "Expected Start Date",
      name: "expected_start_date",
      type: "text",
      filter: false,
    },
    {
      title: "Interview Date",
      name: "interview_date",
      type: "text",
      filter: false,
    },
    {
      title: "Program Start Date",
      name: "start_date",
      type: "text",
      filter: false,
    },
    {
      title: "Case Number",
      name: "case_number",
      type: "text",
      filter: false,
    },
    {
      title: "Notice Date",
      name: "notice_date",
      type: "text",
      filter: false,
    },
    {
      title: "Type",
      name: "processtypes",
      type: "text",
      filter: true,
    },
    {
      title: "Sub Status",
      name: "processsubstatuses",
      type: "text",
      filter: true,
    },
    {
      title: "Responsible Agent",
      name: "agent",
      type: "text",
      filter: true,
    },
  ];

  const {
    accessModule,
    activeFilters,
    gridData,
    gridDetails,
    gridHeader,
    handleFilters,
    handleOpened,
    limit,
    loadingData,
    opened,
    orderBy,
    page,
    pages,
    search,
    setActiveFilters,
    setGridData,
    setGridDetails,
    setGridHeader,
    setLimit,
    setLoadingData,
    setOpened,
    setOrderBy,
    setPage,
    setPages,
    setSearch,
    setTotalRows,
    setSuccessfullyUpdated,
    successfullyUpdated,
    totalRows,
  } = useContext(FullGridContext);

  const pageAccess = accessModule.children.find(
    (el) => el.alias === "enrollment-pre-arrivals"
  );

  const mapPreArrivals = (data) =>
    data.map(
      ({ id, student, contract, status, canceled_at, processes }, index) => {
        const process = processes[0];
        const studentName = student?.name + " " + student?.last_name;
        return {
          show: true,
          id,
          fields: [
            studentName,
            status,
            student?.expectedStartDate || "",
            process?.interview_date || "",
            student?.programs[0]?.start_date || "",
            process?.case_number || "",
            process?.notice_date || "",
            contract?.processtypes?.name || "",
            contract?.processsubstatuses?.name || "",
            contract?.agent?.name || "",
          ],
          selectable: status === "Pending",
          canceled: canceled_at || status !== "Pending",
          page: Math.ceil((index + 1) / limit),
        };
      }
    );

  async function loader() {
    setLoadingData(true);
    const stored = getStoredFilters();
    const extraParams = {};
    if (stored) {
      if (stored.status) extraParams.status = stored.status;
      if (stored.process) extraParams.type = stored.process;
      if (stored.substatus) extraParams.substatus = stored.substatus;
      if (stored.agent) extraParams.agent = stored.agent;
      if (stored.programStartDateFrom)
        extraParams.program_start_date_from = stored.programStartDateFrom;
      if (stored.programStartDateTo)
        extraParams.program_start_date_to = stored.programStartDateTo;
      if (stored.expectedStartDateFrom)
        extraParams.student_expected_start_date_from = stored.expectedStartDateFrom;
      if (stored.expectedStartDateTo)
        extraParams.student_expected_start_date_to = stored.expectedStartDateTo;
      if (stored.interviewDateFrom)
        extraParams.interview_date_from = stored.interviewDateFrom;
      if (stored.interviewDateTo)
        extraParams.interview_date_to = stored.interviewDateTo;
    }
    const data = await getData("pre-arrivals", {
      limit,
      page,
      orderBy,
      setPages,
      setGridData,
      search,
      defaultGridHeader,
      defaultOrderBy,
      setGridDetails,
      extraParams: Object.keys(extraParams).length ? extraParams : null,
    });

    if (!data) {
      setGridData([]);
      setLoadingData(false);
      return;
    }
    setGridData(mapPreArrivals(data));
    setLoadingData(false);
  }

  useEffect(() => {
    setActiveFilters([]);
    setSearch("");
  }, []);

  const applyFiltersToGrid = async (queryString) => {
    setApplyingFilters(true);
    setLoadingData(true);
    try {
      setActiveFilters([]);
      setActiveFilterQuery(queryString);
      const { data } = await api.get(queryString);
      const pagesCalc = Math.ceil((data?.totalRows || 0) / limit) || 1;
      setPages(pagesCalc);
      setGridDetails({
        totalRows: data?.totalRows || 0,
        pages: pagesCalc,
      });
      setGridData(mapPreArrivals(data?.rows || []));
    } catch (err) {
      console.log(err);
    } finally {
      setApplyingFilters(false);
      setLoadingData(false);
    }
  };

  useEffect(() => {
    setActiveFilterQuery(null);

    if (!activeFilterQuery) {
      loader();
    }
  }, [opened, filial, search, limit, page]);

  useEffect(() => {
    if (!orderBy) return;

    if (activeFilterQuery) {
      setLoadingData(true);

      const url = new URL(activeFilterQuery, window.location.origin);
      const params = new URLSearchParams(url.search);

      params.set("orderBy", orderBy.column);
      params.set("orderASC", orderBy.asc ? "ASC" : "DESC");

      const newQueryString = `${url.pathname}?${params.toString()}`;

      api
        .get(newQueryString)
        .then(({ data }) => {
          const pagesCalc = Math.ceil((data?.totalRows || 0) / limit) || 1;
          setPages(pagesCalc);
          setGridDetails({
            totalRows: data?.totalRows || 0,
            pages: pagesCalc,
          });
          setGridData(mapPreArrivals(data?.rows || []));
        })
        .catch((err) => console.log(err))
        .finally(() => setLoadingData(false));
    } else {
      loader();
    }
  }, [orderBy]);

  async function handleApprove() {
    try {
      await api.put(`/pre-arrivals-approve/${selected[0].id}`);
      toast("Student moved to arrivals!", {
        type: "success",
        autoClose: 2000,
      });
      loader();
    } catch (err) {
      toast(err.response.data.error, {
        type: "error",
        autoClose: 3000,
      });
    }
  }

  const selectionFunctions = [];
  selectionFunctions.push({
    title: "Approve",
    alias: "Check",
    fun: handleApprove,
    icon: "Check",
    Page: null,
    opened: false,
    setOpened: () => {},
    selected,
  });

  return (
    <>
      <ModalFilters
        opened={filtersModalOpen}
        applying={applyingFilters}
        onClose={() => {
          setFiltersModalOpen(false);
          setFiltersPinned(
            localStorage.getItem("prearrivals_filters_pinned") === "true"
          );
        }}
        onApplyFilters={applyFiltersToGrid}
      />
      <PageContainer
        FullGridContext={FullGridContext}
        PagePreview={PagePreview}
        pageAccess={pageAccess}
        defaultGridHeader={defaultGridHeader}
        handleNew={false}
        selection={{
          multiple: false,
          selected,
          setSelected,
          functions: selectionFunctions,
        }}
        filtersModal={{
          onOpen: () => setFiltersModalOpen(true),
          viewSelect: filtersPinned ? (
            <div className="flex items-center gap-1 text-orange-500 text-xs font-medium">
              <Pin size={13} className="fill-orange-500" />
              Filters Applied
            </div>
          ) : null,
        }}
      />
    </>
  );
}
